/**
 * Which column, if any, puts a newly created parcel on its creator's book.
 *
 * THE DEFECT THIS EXISTS FOR. A booked user creates an account through the wizard and it
 * is immediately invisible to them — absent from their parcel list and 404 by id. The
 * property was visible because `CRUD_SCOPE_SUBQUERIES.properties` reaches a property
 * through ANY of its parcels' assignments, so a sibling parcel's consultant put it in
 * view; the new parcel carries no assignment of its own and so belongs to nobody.
 *
 * WHY THIS IS NOT SIMPLY "STAMP THE CREATOR". Book membership and money do not line up
 * (src/middleware/assignment-scope.js for the legs; fee-engine / commissions / fee-splits
 * for the money):
 *
 *   role        book leg                                  feeds money?
 *   agent       parcels.consultant_id                      YES
 *               OR clients.assigned_agent_user_id          no
 *               OR properties.commission_agent_user_id     no
 *   analyst     parcels.assistant_id                       NO
 *   consultant  parcels.consultant_id                      YES
 *
 * `parcels.consultant_id` is written straight into `invoice_items.consultant_id` by
 * fee-engine.js and workflows.js, alongside consultant_split_pct / consultant_share, and
 * fee-splits.js resolves the split from (consultant_id + property_id). Commission is only
 * BOOKED when a consultant deal resolves (`commissions.js` does `if (!deal) continue;`),
 * so stamping someone with no deal moves no money — but anyone holding a
 * consultant-DEFAULT deal would silently start earning on every account they happened to
 * create. That is a money change nothing in the UI would announce.
 *
 * So: assign automatically only where it is money-free, and make the money-bearing case a
 * thing a person chose. `assistant_id` and `associate_id` appear in no fee, commission or
 * split path.
 */

/** The analyst book leg — money-free, so it needs no confirmation. */
export const ANALYST_BOOK_COLUMN = 'assistant_id';
/** The agent/consultant book leg — ALSO the money column, so it is opt-in only. */
export const CONSULTANT_BOOK_COLUMN = 'consultant_id';

export interface BookAssignment {
  /** The parcel column to stamp, or null when this role has no per-parcel leg. */
  column: 'assistant_id' | 'consultant_id' | null;
  /** True when stamping can affect commission, and so must be chosen explicitly. */
  bearsMoney: boolean;
  /** Whether the control should start ticked. Money-bearing assignments never do. */
  defaultOn: boolean;
  /** Plain-language label for the control, or null when no control is shown. */
  label: string | null;
  /** The consequence, stated where money is involved. */
  note: string | null;
}

/**
 * How this role gets onto the book of an account they create.
 *
 * A firm-wide role (super_admin / admin / administrator) has no book — `crudScopePredicate`
 * returns null for them and they see everything — so there is nothing to fix and no
 * control to show.
 */
export function bookAssignmentFor(role: string | null | undefined): BookAssignment {
  if (role === 'analyst') {
    return {
      column: ANALYST_BOOK_COLUMN,
      bearsMoney: false,
      defaultOn: true,
      label: 'Add these accounts to my work',
      note: null,
    };
  }
  if (role === 'agent' || role === 'consultant') {
    // ⚠️ DEFAULT-OFF IS ONLY VIABLE BECAUSE THE FK GUARD RESOLVES AT CLIENT GRAIN.
    // The wizard attaches the source document with `PUT /api/documents/:id {parcel_id}`,
    // which is book-validated. If that guard ever asks its question at PARCEL grain
    // instead, an agent who DECLINES this opt-in creates a parcel with no consultant_id
    // — off-book at parcel grain — and the attach 403s. The money-safe default would
    // then be the broken one, and the pressure would be to default this ON to make the
    // feature work at all. At client grain the parcel's client is on the agent's book by
    // construction (it is how they reached the property), so declining costs nothing.
    // The two decisions are only correct together: do not "simplify" the guard to parcel
    // grain without revisiting this default.
    return {
      column: CONSULTANT_BOOK_COLUMN,
      bearsMoney: true,
      defaultOn: false,
      label: 'Assign me as consultant on these accounts',
      note: 'Consultant is also the column fees are split on, so leaving this unticked keeps the accounts off any commission you have configured. Without it, accounts you create here may not appear in your own list.',
    };
  }
  return { column: null, bearsMoney: false, defaultOn: false, label: null, note: null };
}
